import { Button, TextInput } from '@jcm/design-system';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { Box, Collapse, Grid, Input, Typography } from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';
import { useGovernancaContext } from '../../../../../../../context/GovernancaContext';
import CustomAlert from '../../../../../../../shared/components/customAlert/customAlert';
import ModalContainer from '../../../../components/modalContainer/ModalContainer';
import ObrigacaoService from '../../../../services/Obrigacoes/Obrigacoes.service';
import { PerfilFuncionario } from '../../../../ts/enums/FuncaoEnum';
import { TipoDadoEnum } from '../../../../ts/enums/TipoDadoEnum';
import { Anexo, CriarAnexoPayload, CriarAnexosResponse } from '../../../../ts/types/ObrigacaoEtapa/common/Anexos';
import { TEtapa } from '../../../../ts/types/ObrigacaoEtapa/Etapas/Etapa';
import { TObrigacao } from '../../../../ts/types/ObrigacaoEtapa/Obrigacoes/Obrigacoes';
import styles from './index.module.scss';
import ListaAnexos from './ListaAnexos/ListaAnexos';
import { EnumTipoAnexo } from '../../../../ts/enums/TipoAnexoEnum';

interface AnexosProps {
	empresaId?: number | string;
	data: CriarAnexoPayload;
	obrigacao: TObrigacao;
	onChange: (field: keyof CriarAnexoPayload, value: unknown) => void;
	onSave: () => Promise<void>;
	onAtualizar?: () => void;
	isVisible: boolean;
	onClose: () => void;
	origem: 'cartao' | 'formulario';
	setPrecisaAtualizarListagem?: React.Dispatch<React.SetStateAction<boolean>>;
	tipoDado: TipoDadoEnum;
	etapa?: TEtapa;
}

const Anexos: React.FC<AnexosProps> = ({
	obrigacao,
	onChange,
	onSave,
	onAtualizar,
	isVisible,
	onClose,
	origem,
	setPrecisaAtualizarListagem,
	tipoDado,
	etapa,
}) => {
	const { usuario, empresaAtiva } = useGovernancaContext();
	const _obrigacaoService = new ObrigacaoService();

	const [anexos, setAnexos] = useState<Anexo[]>([]);
	const [alert, setAlert] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
	const [aberto, setAberto] = useState(false);
	const [arquivoSelecionado, setArquivoSelecionado] = useState<string | null>(null);
	const [descricaoAnexo, setDescricaoAnexo] = useState<string>('');
	const [exibirMensagem, setExibirMensagem] = useState(false);
	const [loading, setLoading] = useState(false);

	const [mudancaNaListagem, setMudancaNaListagem] = useState(false);

	const sinalizarMudancaNaListagem = () => {
		setMudancaNaListagem(true);
	};
	const inputFileRef = useRef<HTMLInputElement | null>(null);

	const obrigacaoOuEtapaAprovada =
		tipoDado === TipoDadoEnum.Obrigacao ? obrigacao?.aprovada : tipoDado === TipoDadoEnum.Etapa && etapa?.aprovada;

	const isResponsavel =
		tipoDado === TipoDadoEnum.Obrigacao
			? obrigacao?.responsaveis?.some((responsavel) => responsavel.funcionario.funcionarioId === usuario?.funcionarioId)
			: tipoDado === TipoDadoEnum.Etapa &&
				etapa?.responsaveis?.some((responsavel) => responsavel.funcionario.funcionarioId === usuario?.funcionarioId);

	const isAprovador =
		tipoDado === TipoDadoEnum.Obrigacao
			? obrigacao?.aprovadores?.some((aprovador) => aprovador.funcionario.funcionarioId === usuario?.funcionarioId)
			: tipoDado === TipoDadoEnum.Etapa &&
				etapa?.aprovadores?.some((aprovador) => aprovador.funcionario.funcionarioId === usuario?.funcionarioId);

	const isAdmin =
		usuario?.funcao === PerfilFuncionario.AdministradorEmpresa ||
		usuario?.funcao === PerfilFuncionario.AdministradorSistema;

	const podeVisualizarAnexos = isResponsavel || isAprovador || isAdmin;

	useEffect(() => {
		const fetchAnexos = async () => {
			const tipoDadoAnexoReferencia =
				tipoDado === TipoDadoEnum.Obrigacao
					? obrigacao.anexosAcaoObrigacao
					: tipoDado === TipoDadoEnum.Etapa && etapa && obrigacao.children
						? etapa.anexosAcaoObrigacao
						: null;

			if (tipoDadoAnexoReferencia) {
				const novosAnexos = await carregarAnexosAsync(tipoDadoAnexoReferencia || []);
				setAnexos(novosAnexos);
			}
		};

		fetchAnexos();
	}, [obrigacao, etapa]);

	useEffect(() => {
		if (alert) {
			const timer = setTimeout(() => setAlert(null), 3000);
			return () => clearTimeout(timer);
		}
	}, [alert]);

	useEffect(() => {
		if (!isVisible) {
			setMudancaNaListagem(false);
		} else {
			fetchObrigacoes();
		}
	}, [isVisible]);

	const showAlert = (type: 'success' | 'error', message: string) => {
		setAlert({ type, message });
	};

	const resetForm = () => {
		setArquivoSelecionado(null);
		setDescricaoAnexo('');
		setExibirMensagem(false);
		onChange('descricao', '');
		onChange('arquivo', null);
	};

	const carregarAnexosAsync = async (anexosAtualizados: CriarAnexosResponse[] = []) => {
		return anexosAtualizados.map((acaoAnexo) => transformarAnexo(acaoAnexo));
	};

	const transformarAnexo = (acaoAnexo: CriarAnexosResponse) => {
		const { anexo } = acaoAnexo || {};
		return {
			id: anexo?.id || 0,
			nomeOriginal: anexo?.nomeOriginal || 'Nome do arquivo não disponível',
			descricao: anexo?.descricao || 'Sem descrição',
			dataCriacao: anexo?.dataCriacao ? new Date(anexo.dataCriacao).toLocaleDateString() : 'Data não disponível',
			funcionarioId: anexo?.funcionarioId || 0,
			contentType: anexo?.contentType || '',
			extensao: anexo?.extensao || '',
			tipoAnexo: anexo?.tipoAnexo || 0,
			funcionario: anexo?.funcionario || undefined,
			nomeFisico: anexo?.nomeFisico || '',
			arquivo: anexo?.arquivo || null,
			idObrigacao: anexo?.idObrigacao || 0,
		};
	};

	const handleGetUserEmpresaId = () => {
		return usuario?.participacaoEmpresa?.[0]?.empresaId || usuario?.empresaSalvaId;
	};

	const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		if (e.target.files && e.target.files.length > 0) {
			const arquivo = e.target.files[0];
			setArquivoSelecionado(arquivo.name);
			onChange('arquivo', arquivo);
			setExibirMensagem(false);
		}
	};

	const handleSave = async () => {
		setLoading(true);
		try {
			await onSave();
			await fetchObrigacoes();
			sinalizarMudancaNaListagem();
			showAlert('success', 'Anexo salvo com sucesso!');
		} catch {
			showAlert('error', 'Erro ao salvar o anexo.');
		} finally {
			resetForm();
			setLoading(false);
		}
	};

	const fetchObrigacoes = async () => {
		try {
			if (empresaAtiva) {
				const response = await _obrigacaoService.getMinhasObrigacoes(empresaAtiva);
				const novasObrigacoes: TObrigacao[] = response.data.result;

				let obrigacaoAtualizada;

				if (tipoDado === TipoDadoEnum.Obrigacao) {
					obrigacaoAtualizada = novasObrigacoes.find((obs) => obs.id === obrigacao.id);
				}

				if (tipoDado === TipoDadoEnum.Etapa) {
					const encontrandoObrigacao = novasObrigacoes.find((obs) => obs.id === obrigacao.id);

					if (encontrandoObrigacao?.children && etapa) {
						obrigacaoAtualizada = encontrandoObrigacao?.children.find((etp) => etp.id === etapa.id);
					}
				}

				if (obrigacaoAtualizada?.anexosAcaoObrigacao) {
					const anexosTransformados = await carregarAnexosAsync(obrigacaoAtualizada.anexosAcaoObrigacao);
					setAnexos(anexosTransformados);
				}
			}
		} catch (error) {
			console.error('Erro ao buscar obrigações:', error);
		}
	};

	return (
		<ModalContainer
			isVisible={isVisible}
			onClose={() => {
				resetForm();

				if (mudancaNaListagem) {
					if (origem === 'cartao') {
						onAtualizar?.();
					} else if (origem === 'formulario') {
						setPrecisaAtualizarListagem?.(true);
					}
				}
				setMudancaNaListagem(false);
				onClose();
			}}
			className={styles.modalAnexoContainer}
		>
			<div className={styles.modalAnexoContent}>
				<Grid className={styles.containerAnexo}>
					<Grid container spacing={2} className={styles.containerAnexoHeader}>
						<Grid>
							<Typography variant='h6' className={styles.typographyH6}>
								Anexos
							</Typography>
							{exibirMensagem && (
								<Typography variant='h6' className={styles.typographyParagraph}>
									É obrigatório anexar arquivos de evidência para conclusão
								</Typography>
							)}
						</Grid>
						{isResponsavel && obrigacaoOuEtapaAprovada !== 1 && (
							<Grid className={styles.rightAligned}>
								<Button variant='default' type='filled' className={styles.addButton} onClick={() => setAberto(true)}>
									Adicionar anexo
								</Button>
							</Grid>
						)}
					</Grid>

					<Box className={styles.anexosContainer}>
						{isResponsavel && (
							<Collapse in={aberto} className={styles.uploadContainerArea}>
								<Box
									className={styles.uploadArea}
									onDragOver={(e) => e.preventDefault()}
									onDrop={(e) => {
										e.preventDefault();
										const files = e.dataTransfer.files;
										if (files && files.length > 0) {
											handleFileChange({ target: { files } } as React.ChangeEvent<HTMLInputElement>);
										}
									}}
								>
									<Input
										type='file'
										onChange={handleFileChange}
										className={styles.uploadInput}
										disableUnderline
										style={{ display: 'none' }}
										ref={inputFileRef}
										id={`upload-button-file-${obrigacao.id}`}
									/>
									<Button
										variant='default'
										type='outlined'
										className={styles.uploadButton}
										onClick={() => document.getElementById(`upload-button-file-${obrigacao.id}`)?.click()}
									>
										<div className={styles.uploadContainer}>
											<UploadFileIcon className={styles.uploadIcon} />
											<Typography variant='body1' className={styles.uploadTexto}>
												{arquivoSelecionado || 'Clique ou arraste o arquivo para upload'}
											</Typography>
										</div>
									</Button>
								</Box>

								<TextInput
									type='text'
									variant='filled'
									placeholder='Descrição do anexo'
									className={styles.descricaoInputAnexo}
									value={descricaoAnexo}
									onChange={(e) => {
										setDescricaoAnexo(e.target.value);
										onChange('descricao', e.target.value);
										if (!arquivoSelecionado && e.target.value.trim()) {
											setExibirMensagem(true);
										}
									}}
								/>

								<Box className={styles.buttonsContainer}>
									<Button
										variant='error'
										type='outlined'
										onClick={() => setAberto(false)}
										className={styles.cancelButton}
									>
										Cancelar
									</Button>
									<Button
										variant='default'
										type='filled-tonal'
										loading={loading}
										onClick={handleSave}
										className={styles.saveButton}
										disabled={!arquivoSelecionado}
									>
										Salvar
									</Button>
								</Box>
							</Collapse>
						)}
						{podeVisualizarAnexos ? (
							<ListaAnexos
								anexos={anexos}
								onDelete={async (index) => {
									const anexoId = anexos[index].id;
									try {
										const empresaId = handleGetUserEmpresaId();
										if (empresaId !== undefined) {
											const idReferenciaTipoDado =
												tipoDado === TipoDadoEnum.Obrigacao
													? obrigacao.id
													: tipoDado === TipoDadoEnum.Etapa && etapa && obrigacao.children
														? etapa.id
														: null;

											const tipoAnexo: EnumTipoAnexo =
												tipoDado === TipoDadoEnum.Obrigacao
													? EnumTipoAnexo.Obrigacao
													: tipoDado === TipoDadoEnum.Etapa && etapa && obrigacao.children
														? EnumTipoAnexo.EtapaObrigacao
														: EnumTipoAnexo.Nenhum;

											if (idReferenciaTipoDado) {
												await _obrigacaoService.excluirAnexoObrigacao(
													empresaId,
													idReferenciaTipoDado,
													anexoId,
													tipoAnexo,
												);
												setAnexos((prevAnexos) => prevAnexos.filter((_, i) => i !== index));
												showAlert('success', 'Anexo excluído com sucesso!');
												setMudancaNaListagem(true);
											}
										}
									} catch {
										showAlert('error', 'Erro ao excluir o anexo.');
									}
								}}
								onEdit={async (index, descricao) => {
									try {
										const empresaId = handleGetUserEmpresaId();
										if (empresaId !== undefined) {
											const idReferenciaTipoDado =
												tipoDado === TipoDadoEnum.Obrigacao
													? obrigacao.id
													: tipoDado === TipoDadoEnum.Etapa && etapa && obrigacao.children
														? etapa.id
														: null;

											await _obrigacaoService.editarAnexoObrigacao(
												empresaId,
												idReferenciaTipoDado,
												anexos[index].id,
												descricao,
											);

											setAnexos((prev) => {
												const updated = [...prev];
												updated[index].descricao = descricao;
												return updated;
											});
											showAlert('success', 'Edição salva com sucesso!');

											setMudancaNaListagem(true);
										}
									} catch {
										showAlert('error', 'Erro ao salvar a edição.');
									}
								}}
								onSuccess={(message) => showAlert('success', message)}
								onError={(message) => showAlert('error', message)}
								disabled={obrigacaoOuEtapaAprovada === 1 || !isResponsavel}
								usuario={usuario}
							/>
						) : (
							<Typography variant='body1'>Você não tem permissão para visualizar os anexos.</Typography>
						)}

						{alert && <CustomAlert type={alert.type} content={alert.message} />}
					</Box>
				</Grid>
			</div>
		</ModalContainer>
	);
};

export default Anexos;
